import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { CollectedData } from 'src/interfaces/collectedData'
import { ContractorDetails } from 'src/interfaces/contractor'
import { ContractorStepDetailsCardStatus } from 'src/components/contractorStepDetailsCard/constants'
import { mapCollectedDataStatusToCardStatus } from 'src/components/contractorStepDetailsCard/utils'
import { getContractorStepItemState } from './useContractorStepItemState'
import { getContractorStepDetailsBlocksVisibility } from './useContractorStepDetailsBlocksVisibility'
import { StepActionsMenu } from '../../contractor-details/step-actions/StepActionsMenu'
import { SubmissionBlock } from '../types'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { UserPermission } from 'src/constants/user'

dayjs.extend(utc)

const DATE_FORMAT = 'MM/DD/YYYY'

export interface UseStepContentBlocksParams {
  collectedDataItems: CollectedData[]
  contractorDetails: ContractorDetails
  partnerId: string | null
  isRequestApprove?: boolean
  isSubcontractor?: boolean
  onRefreshContractors: () => void | Promise<void>
  openModalForItem: (itemId: string) => void
}

export function useStepContentBlocks({
  collectedDataItems,
  contractorDetails,
  partnerId,
  isRequestApprove,
  isSubcontractor,
  onRefreshContractors,
  openModalForItem,
}: UseStepContentBlocksParams): SubmissionBlock[] {
  const { hasPermission } = useCheckPermissions()

  const canEditStepDates = hasPermission(UserPermission.PartnerDriversEdit)

  return useMemo(() => {
    return collectedDataItems.map((item): SubmissionBlock => {
      const state = getContractorStepItemState({
        collectedData: item,
        contractorDetails,
        isRequestApprove,
        isSubcontractor,
      })
      const visibility = getContractorStepDetailsBlocksVisibility({
        collectedData: item,
        contractorDetails,
        stepCanBeRecollected: state.stepCanBeRecollected,
        stepStatus: state.stepStatus,
        partnerId,
        isRequestApprove,
        isSubcontractor,
      })

      let status = mapCollectedDataStatusToCardStatus(state.stepStatus)

      if (item.skipped) {
        status = ContractorStepDetailsCardStatus.Skipped
      }

      if (state.expiresSoon && status !== ContractorStepDetailsCardStatus.WaitingForApproval) {
        status = ContractorStepDetailsCardStatus.ExpiringSoon
      }

      if (state.isExpired) {
        status = ContractorStepDetailsCardStatus.Expired
      }

      const showExpirationOption = canEditStepDates && visibility.showSetExpirationButton
      const showEffectiveDateOption = canEditStepDates && visibility.showSetEffectiveDateButton
      const showActions =
        showExpirationOption || showEffectiveDateOption || visibility.showDeclineButton

      return {
        status,
        showReviewButtonWithText: visibility.isWaitingForApproval,
        expiresAt: state.expiresAt ? dayjs(state.expiresAt).utc().format(DATE_FORMAT) : undefined,
        effectiveAt: item.effectiveAt
          ? dayjs(item.effectiveAt).utc().format(DATE_FORMAT)
          : undefined,
        collectedData: item,
        onReviewStepDataAction: visibility.showDetails
          ? () => openModalForItem(item.id)
          : undefined,
        actions: showActions ? (
          <StepActionsMenu
            collectedData={item}
            contractorDetails={contractorDetails}
            showDeclineOption={visibility.showDeclineButton}
            showExpirationOption={showExpirationOption}
            showEffectiveDateOption={showEffectiveDateOption}
            onAction={onRefreshContractors}
          />
        ) : undefined,
        showRecollectButton: onRefreshContractors != null && visibility.showRecollectButton,
        showExpirationDate: visibility.showExpirationDate,
        showEffectiveDate: visibility.showEffectiveDate,
      }
    })
  }, [
    collectedDataItems,
    contractorDetails,
    partnerId,
    isRequestApprove,
    isSubcontractor,
    onRefreshContractors,
    openModalForItem,
    canEditStepDates,
  ])
}
